export type CmsStatus = "draft" | "published";

// Mirrors admin_cms_service's shared/configs/constants.js BANNER_TYPES /
// BANNER_TYPE_ASPECT_RATIOS — keep both in sync if either changes.
// Renamed on the backend: "carousel" -> "banners", "features" -> "youMightLike".
export type BannerType = "banners" | "billboard" | "promotional" | "youMightLike";

export const BANNER_TYPE_OPTIONS: { value: BannerType; label: string; aspectRatio: string }[] = [
  { value: "banners", label: "Banners", aspectRatio: "16:9" },
  { value: "billboard", label: "Billboard", aspectRatio: "16:9" },
  { value: "promotional", label: "Promotional", aspectRatio: "7:2" },
  { value: "youMightLike", label: "You Might Like", aspectRatio: "3:4" },
];

export function bannerTypeAspectRatio(type?: BannerType | string | null): string | undefined {
  return BANNER_TYPE_OPTIONS.find((option) => option.value === type)?.aspectRatio;
}

export function bannerTypeLabel(type?: BannerType | string | null): string {
  return BANNER_TYPE_OPTIONS.find((option) => option.value === type)?.label || "Banners";
}

export interface CmsMeta {
  [key: string]: any;
}

// Audience-targeting dimensions, keyed by dimension name to an array of
// master ids (or [0] for "All") - see shared/utils/content-targeting.js.
// Returned by getById in place of `meta`, which the backend discards on read.
export type CmsTargeting = Record<string, number[]>;

export interface News {
  id: number;
  title: string;
  slug?: string | null;
  content?: string | null;
  excerpt?: string | null;
  status?: CmsStatus | null;
  featured_image_id?: number | null;
  featured_image_web_id?: number | null;
  video_url?: string | null;
  news_link?: string | null;
  sort_order?: number | null;
  is_active?: boolean | null;
  active_date?: string | null;
  expire_date?: string | null;
  published_at?: string | null;
  author_id?: number | null;
  meta?: CmsMeta | null;
  targeting?: CmsTargeting | null;
  created_at?: string | null;
  updated_at?: string | null;
  featured_image?: Media | null;
  featured_image_web?: Media | null;
  news_source_id?: number | null;
  source_name?: string | null;
  source_url?: string | null;
  source_icon?: string | null;
  source_priority?: number | null;
  // Present on getNewsById's response only (the edit screen), scoped to the
  // `locale` header's language.
  language?: string;
  is_translated?: boolean;
  available_languages?: string[];
  // No. of users who have viewed this article in the mobile app.
  view_count?: number;
}

export interface NewsSource {
  id: number;
  source_code: string;
  // Channel name in the `locale` header's language, falling back to English.
  name: string;
  source_url?: string | null;
  source_icon?: string | null;
  source_priority?: number | null;
  is_active?: boolean;
}

export interface Banner {
  id: number;
  title: string;
  slug?: string | null;
  content?: string | null;
  status?: CmsStatus | null;
  banner_image_id?: number | null;
  banner_image_web_id?: number | null;
  video_url?: string | null;
  link_url?: string | null;
  banner_type?: BannerType | null;
  news_id?: number | null;
  is_default?: boolean | null;
  sort_order?: number | null;
  is_active?: boolean | null;
  active_date?: string | null;
  expire_date?: string | null;
  published_at?: string | null;
  author_id?: number | null;
  meta?: CmsMeta | null;
  targeting?: CmsTargeting | null;
  created_at?: string | null;
  updated_at?: string | null;
  banner_image?: Media | null;
  banner_image_web?: Media | null;
}

export interface AmiForYou {
  id: number;
  title: string;
  description?: string | null;
  status: boolean;
  icon_url?: string | null;
  link?: string | null;
  author_id?: number | null;
  org_id?: number | null;
  targeting?: CmsTargeting | null;
  created_at?: string | null;
  updated_at?: string | null;
  // Present on getAmiForYouById's response only (the edit screen), scoped to
  // the `locale` header's language.
  language?: string;
  is_translated?: boolean;
  available_languages?: string[];
}

export interface AppPollingOption {
  id: number;
  text: string;
  // Present on getAppPollingById's response only.
  is_translated?: boolean;
  available_languages?: string[];
  sort_order?: number;
  vote_count?: number;
  vote_percentage?: number;
}

export interface AppPolling {
  id: number;
  question: string;
  status: boolean;
  author_id?: number | null;
  org_id?: number | null;
  targeting?: CmsTargeting | null;
  // Present on getAppPollingById's response only (the edit/details screen).
  options?: AppPollingOption[];
  total_responses?: number;
  created_at?: string | null;
  updated_at?: string | null;
  language?: string;
  is_translated?: boolean;
  available_languages?: string[];
}

export interface Media {
  id: number;
  title?: string | null;
  file_path?: string | null;
  file_url: string;
  mime_type?: string | null;
  alt_text?: string | null;
  file_size?: number | null;
  width?: number | null;
  height?: number | null;
  metadata?: CmsMeta | null;
  uploaded_by?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

// e.g. (1920, 1080) -> "1920 x 1080 px (16:9)" - lets the user judge, while
// picking existing media, whether it actually matches the ratio a picker's
// recommendedAspectRatio is asking for.
export function mediaDimensionsLabel(item: Pick<Media, "width" | "height">): string | null {
  if (!item.width || !item.height) return null;
  const divisor = gcd(item.width, item.height) || 1;
  return `${item.width} x ${item.height} px (${item.width / divisor}:${item.height / divisor})`;
}

export interface CmsListResult<T> {
  total: number;
  list: T[];
}
